/**
 * ContentRepository.ts
 * Loads tunable content (balance, spells, levels) at boot.
 *
 * Strategy: start from bundled DEFAULT_CONTENT, then — if Supabase is
 * configured — overlay any rows from the `balance`, `spells`, and `levels`
 * tables. Any failure (no config, offline, query error) silently keeps the
 * defaults, so boot never blocks on the network.
 */

import type { BalanceValues, FutureSong, GameContent, LevelDefinition, SpellDefinition, SpellId } from '../types';
import { supabase, isSupabaseConfigured } from './supabase';
import { DEFAULT_CONTENT } from './content/defaults';

export class ContentRepository {
  /** Load game content, preferring Supabase and falling back to defaults. */
  static async load(): Promise<GameContent> {
    if (!isSupabaseConfigured || !supabase) {
      return DEFAULT_CONTENT;
    }

    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('content load timeout')), 3000)
    );

    try {
      return await Promise.race([this.loadFromSupabase(), timeout]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[data] content load failed; using defaults.', err);
      return DEFAULT_CONTENT;
    }
  }

  private static async loadFromSupabase(): Promise<GameContent> {
    const [balance, spells, levels] = await Promise.all([
      this.loadBalance(),
      this.loadSpells(),
      this.loadLevels(),
    ]);
    const futureSongs = await this.loadFutureSongs();

    return {
      source: 'supabase',
      balance: { ...DEFAULT_CONTENT.balance, ...balance },
      spells: spells.length ? spells : DEFAULT_CONTENT.spells,
      levels: levels.length ? levels : DEFAULT_CONTENT.levels,
      futureSongs: futureSongs.length ? futureSongs : DEFAULT_CONTENT.futureSongs,
    };
  }

  /** `balance` table: key/value rows overriding individual balance fields. */
  private static async loadBalance(): Promise<Partial<BalanceValues>> {
    const { data, error } = await supabase!.from('balance').select('key, value');
    if (error || !data) return {};
    const out: Partial<BalanceValues> = {};
    for (const row of data as Array<{ key: string; value: number }>) {
      (out as Record<string, number>)[row.key] = row.value;
    }
    return out;
  }

  private static async loadSpells(): Promise<SpellDefinition[]> {
    const { data, error } = await supabase!
      .from('spells')
      .select('id, name, cooldown, reputation_cost, description');
    if (error || !data) return [];
    return (data as Array<Record<string, unknown>>).map((r) => ({
      id: r.id as SpellId,
      name: r.name as string,
      cooldown: r.cooldown as number,
      reputationCost: r.reputation_cost as number,
      description: r.description as string,
    }));
  }

  private static async loadLevels(): Promise<LevelDefinition[]> {
    const { data, error } = await supabase!
      .from('levels')
      .select('id, name, description, waves')
      .order('id', { ascending: true });
    if (error || !data) return [];
    return (data as Array<Record<string, unknown>>).map((r) => ({
      id: r.id as string,
      name: r.name as string,
      description: r.description as string,
      waves: (r.waves as LevelDefinition['waves']) ?? [],
    }));
  }

  private static async loadFutureSongs(): Promise<FutureSong[]> {
    const rich = await supabase!
      .from('future_songs')
      .select('title, sort_order, audio_url, audio_path, audio_mime')
      .order('sort_order', { ascending: true })
      .order('title', { ascending: true });
    if (!rich.error && rich.data) {
      return (rich.data as Array<{
        title: string | null;
        audio_url?: string | null;
        audio_path?: string | null;
        audio_mime?: string | null;
      }>)
        .map(row => ({
          title: (row.title ?? '').trim(),
          audioUrl: (row.audio_url ?? '').trim() || undefined,
          audioPath: (row.audio_path ?? '').trim() || undefined,
          audioMime: (row.audio_mime ?? '').trim() || undefined,
        }))
        .filter(row => Boolean(row.title));
    }

    // Migration 0008 adds snippet columns. If it has not been applied yet,
    // keep the game loading title-only songs instead of falling back entirely.
    const fallback = await supabase!
      .from('future_songs')
      .select('title, sort_order')
      .order('sort_order', { ascending: true })
      .order('title', { ascending: true });
    if (fallback.error || !fallback.data) return [];
    return (fallback.data as Array<{ title: string | null }>)
      .map(row => ({ title: (row.title ?? '').trim() }))
      .filter(row => Boolean(row.title));
  }
}
